import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  createInitialXiangqiGame,
  getXiangqiLegalMovesForPiece,
  applyXiangqiMove,
  getBestAIMove,
  type XiangqiGameState,
  type XiangqiPiece,
  type XiangqiColor,
  type XiangqiPieceType,
} from '@gamengayxua/shared';
import { api } from '../../api/client.ts';

interface XiangqiGameProps {
  onBackToHub: () => void;
}

type GameMode = 'ai' | 'pvp';
type AIDifficulty = 'easy' | 'medium';
type PieceLabelMode = 'hanzi' | 'vietnamese';

// Authentic piece character display mapping
const PIECE_SYMBOLS: Record<XiangqiPieceType, { redChar: string; blackChar: string; viName: string }> = {
  general: { redChar: '帥', blackChar: '將', viName: 'Tướng' },
  advisor: { redChar: '仕', blackChar: '士', viName: 'Sĩ' },
  elephant: { redChar: '相', blackChar: '象', viName: 'Tượng' },
  horse: { redChar: '傌', blackChar: '馬', viName: 'Mã' },
  chariot: { redChar: '俥', blackChar: '車', viName: 'Xe' },
  cannon: { redChar: '炮', blackChar: '砲', viName: 'Pháo' },
  soldier: { redChar: '兵', blackChar: '卒', viName: 'Tốt' },
};

// Procedural Web Audio Sound Effects
function playWoodClick() {
  try {
    const AudioCtx = window.AudioContext || (window as any).webkitAudioContext;
    if (!AudioCtx) return;
    const ctx = new AudioCtx();
    const osc = ctx.createOscillator();
    const gain = ctx.createGain();
    osc.type = 'triangle';
    osc.frequency.setValueAtTime(420, ctx.currentTime);
    osc.frequency.exponentialRampToValueAtTime(70, ctx.currentTime + 0.08);
    gain.gain.setValueAtTime(0.7, ctx.currentTime);
    gain.gain.exponentialRampToValueAtTime(0.01, ctx.currentTime + 0.08);
    osc.connect(gain);
    gain.connect(ctx.destination);
    osc.start();
    osc.stop(ctx.currentTime + 0.09);
  } catch {}
}

function playCheckSound() {
  try {
    const AudioCtx = window.AudioContext || (window as any).webkitAudioContext;
    if (!AudioCtx) return;
    const ctx = new AudioCtx();
    const osc = ctx.createOscillator();
    const gain = ctx.createGain();
    osc.type = 'sine';
    osc.frequency.setValueAtTime(780, ctx.currentTime);
    osc.frequency.setValueAtTime(1046, ctx.currentTime + 0.1);
    gain.gain.setValueAtTime(0.5, ctx.currentTime);
    gain.gain.exponentialRampToValueAtTime(0.01, ctx.currentTime + 0.28);
    osc.connect(gain);
    gain.connect(ctx.destination);
    osc.start();
    osc.stop(ctx.currentTime + 0.29);
  } catch {}
}

export const XiangqiGame: React.FC<XiangqiGameProps> = ({ onBackToHub }) => {
  const [game, setGame] = useState<XiangqiGameState>(createInitialXiangqiGame());
  const [selectedPiece, setSelectedPiece] = useState<XiangqiPiece | null>(null);
  const [legalMoves, setLegalMoves] = useState<{ toRow: number; toCol: number }[]>([]);
  const [gameMode, setGameMode] = useState<GameMode>('ai');
  const [aiDifficulty, setAiDifficulty] = useState<AIDifficulty>('medium');
  const [pieceLabelMode, setPieceLabelMode] = useState<PieceLabelMode>('hanzi');
  const [isAiThinking, setIsAiThinking] = useState<boolean>(false);
  const [historyStack, setHistoryStack] = useState<XiangqiGameState[]>([]);
  const [hintMove, setHintMove] = useState<{ fromRow: number; fromCol: number; toRow: number; toCol: number } | null>(null);

  // SVG dimensions
  const BOARD_WIDTH = 450;
  const BOARD_HEIGHT = 500;
  const MARGIN_X = 25;
  const MARGIN_Y = 25;
  const STEP_X = 50; // (450 - 50) / 8 = 50
  const STEP_Y = 50; // (500 - 50) / 9 = 50

  const getCoordX = (col: number) => MARGIN_X + col * STEP_X;
  const getCoordY = (row: number) => MARGIN_Y + row * STEP_Y;

  // Captured pieces calculation
  const capturedRed = useMemo(() => {
    return game.moveHistory
      .filter((m) => m.captured && m.captured.color === 'red')
      .map((m) => m.captured!);
  }, [game.moveHistory]);

  const capturedBlack = useMemo(() => {
    return game.moveHistory
      .filter((m) => m.captured && m.captured.color === 'black')
      .map((m) => m.captured!);
  }, [game.moveHistory]);

  // AI Turn Trigger
  useEffect(() => {
    if (gameMode === 'ai' && game.turn === 'black' && !game.winner && !isAiThinking) {
      setIsAiThinking(true);
      const timer = setTimeout(() => {
        const depth = aiDifficulty === 'easy' ? 1 : 2;
        const aiMove = getBestAIMove(game, depth);
        if (aiMove) {
          setHistoryStack((prev) => [...prev, game]);
          const nextState = applyXiangqiMove(game, aiMove.fromRow, aiMove.fromCol, aiMove.toRow, aiMove.toCol);
          setGame(nextState);
          setSelectedPiece(null);
          setLegalMoves([]);
          setHintMove(null);

          playWoodClick();
          if (nextState.isCheck) playCheckSound();

          if (nextState.winner) {
            api.saveGameProgress('xiangqi', { winner: nextState.winner }, `Ván cờ kết thúc: ${nextState.winner === 'red' ? 'Đỏ thắng' : 'Đen thắng'}`).catch(() => {});
          }
        }
        setIsAiThinking(false);
      }, 400);

      return () => clearTimeout(timer);
    }
  }, [game, gameMode, aiDifficulty, isAiThinking]);

  // Click on intersection or piece
  const handlePointClick = (row: number, col: number) => {
    if (game.winner || (gameMode === 'ai' && game.turn === 'black')) return;

    const clickedPiece = game.pieces.find((p) => p.row === row && p.col === col);

    // If clicking own piece, select it
    if (clickedPiece && clickedPiece.color === game.turn) {
      setSelectedPiece(clickedPiece);
      const moves = getXiangqiLegalMovesForPiece(clickedPiece, game.pieces);
      setLegalMoves(moves);
      setHintMove(null);
      return;
    }

    // If a piece is selected and clicking a valid destination
    if (selectedPiece) {
      const isDestinationValid = legalMoves.some((m) => m.toRow === row && m.toCol === col);
      if (isDestinationValid) {
        setHistoryStack((prev) => [...prev, game]);
        const nextState = applyXiangqiMove(game, selectedPiece.row, selectedPiece.col, row, col);
        setGame(nextState);
        setSelectedPiece(null);
        setLegalMoves([]);
        setHintMove(null);

        playWoodClick();
        if (nextState.isCheck) playCheckSound();

        if (nextState.winner) {
          api.saveGameProgress('xiangqi', { winner: nextState.winner }, `Ván cờ: ${nextState.winner === 'red' ? 'Đỏ thắng' : 'Đen thắng'}`).catch(() => {});
        }
        return;
      }
    }

    // Deselect if clicking elsewhere
    setSelectedPiece(null);
    setLegalMoves([]);
  };

  // Undo Move
  const handleUndo = () => {
    if (historyStack.length === 0 || isAiThinking) return;

    if (gameMode === 'ai') {
      if (historyStack.length >= 2) {
        const targetState = historyStack[historyStack.length - 2];
        setHistoryStack((prev) => prev.slice(0, prev.length - 2));
        setGame(targetState);
      } else {
        const targetState = historyStack[0];
        setHistoryStack([]);
        setGame(targetState);
      }
    } else {
      const targetState = historyStack[historyStack.length - 1];
      setHistoryStack((prev) => prev.slice(0, prev.length - 1));
      setGame(targetState);
    }

    setSelectedPiece(null);
    setLegalMoves([]);
    setHintMove(null);
  };

  // Reset Game
  const handleNewGame = () => {
    setGame(createInitialXiangqiGame());
    setSelectedPiece(null);
    setLegalMoves([]);
    setHistoryStack([]);
    setHintMove(null);
  };

  // Hint Move
  const handleHint = () => {
    if (game.winner || isAiThinking) return;
    const best = getBestAIMove(game, 2);
    if (best) {
      setHintMove(best);
      const piece = game.pieces.find((p) => p.row === best.fromRow && p.col === best.fromCol);
      if (piece) {
        setSelectedPiece(piece);
        setLegalMoves(getXiangqiLegalMovesForPiece(piece, game.pieces));
      }
    }
  };

  return (
    <div className="max-w-md mx-auto p-3 space-y-3 select-none" style={{ paddingTop: 'calc(10px + env(safe-area-inset-top, 0px))' }}>
      {/* Top Header */}
      <div className="flex items-center justify-between pb-2 border-b border-slate-800">
        <button
          onClick={onBackToHub}
          className="px-3.5 py-2 text-sm font-bold bg-slate-800/90 hover:bg-slate-700 text-slate-200 rounded-lg flex items-center space-x-1.5 active:scale-95 transition-transform"
        >
          <span className="text-base">←</span>
          <span>GameHub</span>
        </button>

        <div className="flex items-center space-x-2">
          <span className="text-base font-black text-amber-400 tracking-wide flex items-center space-x-1.5">
            <span>♟️</span>
            <span>CỜ TƯỚNG KỲ ĐẠO</span>
          </span>
          <span className="text-xs px-2.5 py-0.5 rounded-full bg-emerald-500/20 text-emerald-300 font-bold border border-emerald-500/30">
            FULL ENGINE
          </span>
        </div>
      </div>

      {/* Control Bar: Mode + Difficulty + Font Mode Toggle */}
      <div className="bg-slate-900/90 p-2 rounded-xl border border-slate-800 flex items-center justify-between text-xs sm:text-sm">
        <div className="flex items-center space-x-1">
          <button
            onClick={() => setGameMode('ai')}
            className={`px-2.5 py-1.5 rounded-lg font-bold transition-all ${
              gameMode === 'ai' ? 'bg-amber-500 text-slate-950 font-black shadow-md' : 'text-slate-400 hover:text-slate-200'
            }`}
          >
            🤖 Đấu Máy
          </button>
          <button
            onClick={() => setGameMode('pvp')}
            className={`px-2.5 py-1.5 rounded-lg font-bold transition-all ${
              gameMode === 'pvp' ? 'bg-amber-500 text-slate-950 font-black shadow-md' : 'text-slate-400 hover:text-slate-200'
            }`}
          >
            👥 2 Người
          </button>
        </div>

        <div className="flex items-center space-x-1">
          {gameMode === 'ai' && (
            <div className="flex items-center space-x-0.5 bg-slate-950 p-1 rounded-lg border border-slate-800 text-xs">
              <button
                onClick={() => setAiDifficulty('easy')}
                className={`px-2 py-0.5 rounded font-bold ${
                  aiDifficulty === 'easy' ? 'bg-cyan-500 text-slate-950' : 'text-slate-400'
                }`}
              >
                Dễ
              </button>
              <button
                onClick={() => setAiDifficulty('medium')}
                className={`px-2 py-0.5 rounded font-bold ${
                  aiDifficulty === 'medium' ? 'bg-cyan-500 text-slate-950' : 'text-slate-400'
                }`}
              >
                Khá
              </button>
            </div>
          )}

          {/* Toggle Hanzi vs Vietnamese pieces */}
          <button
            onClick={() => setPieceLabelMode(pieceLabelMode === 'hanzi' ? 'vietnamese' : 'hanzi')}
            className="px-2 py-1 rounded-lg bg-slate-800 text-amber-300 font-bold border border-slate-700 text-xs hover:bg-slate-700"
            title="Đổi kiểu chữ trên quân cờ"
          >
            {pieceLabelMode === 'hanzi' ? '🀄 Hán' : '🇻🇳 Việt'}
          </button>
        </div>
      </div>

      {/* Turn & Check Notification Banner */}
      <div className="flex items-center justify-between px-3 py-2 bg-slate-900/90 rounded-xl border border-slate-800 text-xs sm:text-sm font-bold">
        <div className="flex items-center space-x-2">
          <div className={`w-3.5 h-3.5 rounded-full ${game.turn === 'red' ? 'bg-rose-500 shadow-md shadow-rose-500/60' : 'bg-slate-200 shadow-md'}`} />
          <span className={game.turn === 'red' ? 'text-rose-400 font-black' : 'text-slate-100 font-black'}>
            Lượt: {game.turn === 'red' ? 'BÊN ĐỎ (Bạn)' : 'BÊN ĐEN (Đối thủ)'}
          </span>
          {isAiThinking && (
            <span className="text-cyan-400 text-xs animate-pulse font-bold">
              (Máy đang tính...)
            </span>
          )}
        </div>

        {game.isCheck && !game.winner && (
          <span className="px-2.5 py-0.5 bg-rose-600/30 text-rose-300 rounded-full border border-rose-500 text-xs font-black animate-bounce">
            ⚠️ CHIẾU TƯỚNG!
          </span>
        )}

        {game.winner && (
          <span className="px-2.5 py-0.5 bg-amber-500/30 text-amber-300 rounded-full border border-amber-500 text-xs font-black">
            🏆 {game.winner === 'red' ? 'ĐỎ THẮNG!' : 'ĐEN THẮNG!'}
          </span>
        )}
      </div>

      {/* XIANGQI SVG BOARD (Authentic Traditional Wood Design) */}
      <div className="relative rounded-2xl overflow-hidden shadow-2xl border-4 border-[#4a2e12] bg-[#d9af6b]">
        <svg
          viewBox={`0 0 ${BOARD_WIDTH} ${BOARD_HEIGHT}`}
          className="w-full h-auto block select-none"
          style={{
            background: 'linear-gradient(135deg, #e4ba7b 0%, #d4a766 50%, #c49652 100%)',
          }}
        >
          {/* Subtle Outer Border */}
          <rect
            x={10}
            y={10}
            width={BOARD_WIDTH - 20}
            height={BOARD_HEIGHT - 20}
            fill="none"
            stroke="#5a3512"
            strokeWidth="3"
            rx="8"
          />
          <rect
            x={14}
            y={14}
            width={BOARD_WIDTH - 28}
            height={BOARD_HEIGHT - 28}
            fill="none"
            stroke="rgba(255, 235, 175, 0.4)"
            strokeWidth="1"
            rx="6"
          />

          {/* 10 Horizontal Grid Lines */}
          {Array.from({ length: 10 }).map((_, r) => {
            const y = getCoordY(r);
            return (
              <line
                key={`h-${r}`}
                x1={getCoordX(0)}
                y1={y}
                x2={getCoordX(8)}
                y2={y}
                stroke="#5a3512"
                strokeWidth="1.6"
              />
            );
          })}

          {/* 9 Vertical Grid Lines (Interrupted by River in columns 1..7) */}
          {Array.from({ length: 9 }).map((_, c) => {
            const x = getCoordX(c);
            if (c === 0 || c === 8) {
              return (
                <line
                  key={`v-${c}`}
                  x1={x}
                  y1={getCoordY(0)}
                  x2={x}
                  y2={getCoordY(9)}
                  stroke="#5a3512"
                  strokeWidth="1.6"
                />
              );
            } else {
              return (
                <g key={`v-${c}`}>
                  {/* Top Black side */}
                  <line
                    x1={x}
                    y1={getCoordY(0)}
                    x2={x}
                    y2={getCoordY(4)}
                    stroke="#5a3512"
                    strokeWidth="1.6"
                  />
                  {/* Bottom Red side */}
                  <line
                    x1={x}
                    y1={getCoordY(5)}
                    x2={x}
                    y2={getCoordY(9)}
                    stroke="#5a3512"
                    strokeWidth="1.6"
                  />
                </g>
              );
            }
          })}

          {/* The River (Sở Hà - Hán Giới) in the exact middle */}
          <g opacity="0.85">
            <text
              x={getCoordX(2)}
              y={(getCoordY(4) + getCoordY(5)) / 2 + 7}
              fill="#5a3512"
              fontSize="16"
              fontWeight="900"
              fontFamily="'Be Vietnam Pro', serif"
              textAnchor="middle"
              letterSpacing="2"
            >
              楚 河 (SỞ HÀ)
            </text>
            <text
              x={getCoordX(6)}
              y={(getCoordY(4) + getCoordY(5)) / 2 + 7}
              fill="#5a3512"
              fontSize="16"
              fontWeight="900"
              fontFamily="'Be Vietnam Pro', serif"
              textAnchor="middle"
              letterSpacing="2"
            >
              漢 界 (HÁN GIỚI)
            </text>
          </g>

          {/* Black Palace Diagonals (Cửu Cung Đen: rows 0..2, cols 3..5) */}
          <line
            x1={getCoordX(3)}
            y1={getCoordY(0)}
            x2={getCoordX(5)}
            y2={getCoordY(2)}
            stroke="#5a3512"
            strokeWidth="1.4"
          />
          <line
            x1={getCoordX(5)}
            y1={getCoordY(0)}
            x2={getCoordX(3)}
            y2={getCoordY(2)}
            stroke="#5a3512"
            strokeWidth="1.4"
          />

          {/* Red Palace Diagonals (Cửu Cung Đỏ: rows 7..9, cols 3..5) */}
          <line
            x1={getCoordX(3)}
            y1={getCoordY(7)}
            x2={getCoordX(5)}
            y2={getCoordY(9)}
            stroke="#5a3512"
            strokeWidth="1.4"
          />
          <line
            x1={getCoordX(5)}
            y1={getCoordY(7)}
            x2={getCoordX(3)}
            y2={getCoordY(9)}
            stroke="#5a3512"
            strokeWidth="1.4"
          />

          {/* Last Move Trail Highlight */}
          {game.lastMove && (
            <g>
              <circle
                cx={getCoordX(game.lastMove.fromCol)}
                cy={getCoordY(game.lastMove.fromRow)}
                r="22"
                fill="none"
                stroke="#0284c7"
                strokeWidth="2.5"
                strokeDasharray="4 3"
                opacity="0.85"
              />
              <circle
                cx={getCoordX(game.lastMove.toCol)}
                cy={getCoordY(game.lastMove.toRow)}
                r="22"
                fill="none"
                stroke="#0284c7"
                strokeWidth="2.5"
                opacity="0.85"
              />
            </g>
          )}

          {/* Hint Move Highlight */}
          {hintMove && (
            <g>
              <line
                x1={getCoordX(hintMove.fromCol)}
                y1={getCoordY(hintMove.fromRow)}
                x2={getCoordX(hintMove.toCol)}
                y2={getCoordY(hintMove.toRow)}
                stroke="#f59e0b"
                strokeWidth="3.5"
                strokeDasharray="5 3"
              />
              <circle
                cx={getCoordX(hintMove.toCol)}
                cy={getCoordY(hintMove.toRow)}
                r="22"
                fill="rgba(245, 158, 11, 0.25)"
                stroke="#f59e0b"
                strokeWidth="2"
              />
            </g>
          )}

          {/* Clickable Intersection Grid Points for Move Destinations */}
          {legalMoves.map((m) => {
            const x = getCoordX(m.toCol);
            const y = getCoordY(m.toRow);
            const isCapture = game.pieces.some((p) => p.row === m.toRow && p.col === m.toCol);

            return (
              <g
                key={`dest-${m.toRow}-${m.toCol}`}
                onClick={() => handlePointClick(m.toRow, m.toCol)}
                className="cursor-pointer"
              >
                {isCapture ? (
                  <circle
                    cx={x}
                    cy={y}
                    r="23"
                    fill="rgba(239, 68, 68, 0.35)"
                    stroke="#ef4444"
                    strokeWidth="3"
                    className="animate-pulse"
                  />
                ) : (
                  <circle
                    cx={x}
                    cy={y}
                    r="8.5"
                    fill="#10b981"
                    stroke="#ffffff"
                    strokeWidth="2"
                    className="animate-pulse"
                  />
                )}
              </g>
            );
          })}

          {/* 32 AUTHENTIC CIRCULAR WOODEN PIECES */}
          {game.pieces.map((piece) => {
            const x = getCoordX(piece.col);
            const y = getCoordY(piece.row);
            const isRed = piece.color === 'red';
            const isSelected = selectedPiece?.id === piece.id;
            const sym = PIECE_SYMBOLS[piece.type];
            const char = isRed ? sym.redChar : sym.blackChar;

            return (
              <g
                key={piece.id}
                onClick={() => handlePointClick(piece.row, piece.col)}
                className="cursor-pointer transition-transform"
                style={{ filter: isSelected ? 'drop-shadow(0 0 8px #f59e0b)' : 'drop-shadow(0 3px 4px rgba(0,0,0,0.4))' }}
              >
                {/* Piece Base Wooden Disc */}
                <circle
                  cx={x}
                  cy={y}
                  r="21.5"
                  fill="url(#woodPieceGrad)"
                  stroke={isSelected ? '#f59e0b' : isRed ? '#b21010' : '#1e293b'}
                  strokeWidth={isSelected ? '3' : '2'}
                />

                {/* Inner Decorative Bevel Ring */}
                <circle
                  cx={x}
                  cy={y}
                  r="18"
                  fill="none"
                  stroke={isRed ? 'rgba(178, 16, 16, 0.4)' : 'rgba(30, 41, 59, 0.4)'}
                  strokeWidth="1"
                />

                {/* Main Character: Either authentic Hanzi OR bold Vietnamese text */}
                {pieceLabelMode === 'hanzi' ? (
                  <text
                    x={x}
                    y={y}
                    fill={isRed ? '#b21010' : '#0f172a'}
                    fontSize="23"
                    fontWeight="900"
                    fontFamily="'Be Vietnam Pro', 'Kaiti', serif"
                    textAnchor="middle"
                    dominantBaseline="central"
                  >
                    {char}
                  </text>
                ) : (
                  <text
                    x={x}
                    y={y}
                    fill={isRed ? '#b21010' : '#0f172a'}
                    fontSize={piece.type === 'general' || piece.type === 'elephant' ? '11.5' : '13'}
                    fontWeight="900"
                    fontFamily="'Be Vietnam Pro', sans-serif"
                    textAnchor="middle"
                    dominantBaseline="central"
                  >
                    {sym.viName.toUpperCase()}
                  </text>
                )}
              </g>
            );
          })}

          {/* Gradients Definition */}
          <defs>
            <radialGradient id="woodPieceGrad" cx="35%" cy="35%" r="70%">
              <stop offset="0%" stopColor="#fffbf0" />
              <stop offset="50%" stopColor="#f7ebcf" />
              <stop offset="100%" stopColor="#ecd49f" />
            </radialGradient>
          </defs>
        </svg>
      </div>

      {/* Captured Trays (Red & Black) */}
      <div className="grid grid-cols-2 gap-2 text-xs">
        <div className="flex items-center space-x-1.5 px-2.5 py-1.5 bg-slate-900/60 rounded-lg border border-slate-800 overflow-x-auto no-scrollbar">
          <span className="text-slate-400 font-bold text-[11px] whitespace-nowrap">Đỏ bắt:</span>
          {capturedBlack.length === 0 ? (
            <span className="text-slate-600 text-[11px] italic">0</span>
          ) : (
            capturedBlack.map((p, idx) => (
              <span key={idx} className="px-1.5 py-0.5 rounded bg-slate-800 text-slate-200 border border-slate-700 text-[11px] font-bold">
                {pieceLabelMode === 'hanzi' ? PIECE_SYMBOLS[p.type].blackChar : PIECE_SYMBOLS[p.type].viName}
              </span>
            ))
          )}
        </div>

        <div className="flex items-center space-x-1.5 px-2.5 py-1.5 bg-slate-900/60 rounded-lg border border-slate-800 overflow-x-auto no-scrollbar">
          <span className="text-slate-400 font-bold text-[11px] whitespace-nowrap">Đen bắt:</span>
          {capturedRed.length === 0 ? (
            <span className="text-slate-600 text-[11px] italic">0</span>
          ) : (
            capturedRed.map((p, idx) => (
              <span key={idx} className="px-1.5 py-0.5 rounded bg-rose-950/60 text-rose-300 border border-rose-800/80 text-[11px] font-bold">
                {pieceLabelMode === 'hanzi' ? PIECE_SYMBOLS[p.type].redChar : PIECE_SYMBOLS[p.type].viName}
              </span>
            ))
          )}
        </div>
      </div>

      {/* Action Buttons: Undo, New Game, Hint */}
      <div className="grid grid-cols-3 gap-2">
        <button
          onClick={handleUndo}
          disabled={historyStack.length === 0 || isAiThinking}
          className="py-3 px-2 rounded-xl bg-slate-900/90 hover:bg-slate-800 disabled:opacity-40 text-slate-200 border border-slate-700 font-bold text-xs sm:text-sm flex items-center justify-center space-x-1.5 active:scale-95 transition-transform"
        >
          <span>↩️</span>
          <span>Đi Lại</span>
        </button>

        <button
          onClick={handleHint}
          disabled={!!game.winner || isAiThinking}
          className="py-3 px-2 rounded-xl bg-slate-900/90 hover:bg-slate-800 disabled:opacity-40 text-amber-300 border border-slate-700 font-bold text-xs sm:text-sm flex items-center justify-center space-x-1.5 active:scale-95 transition-transform"
        >
          <span>💡</span>
          <span>Gợi Ý</span>
        </button>

        <button
          onClick={handleNewGame}
          className="py-3 px-2 rounded-xl bg-amber-500/20 hover:bg-amber-500/30 text-amber-300 border border-amber-500/40 font-black text-xs sm:text-sm flex items-center justify-center space-x-1.5 active:scale-95 transition-transform"
        >
          <span>🔄</span>
          <span>Ván Mới</span>
        </button>
      </div>

      {/* Move History / Notation Record */}
      <div className="bg-slate-900/90 p-3 rounded-xl border border-slate-800 space-y-1.5 text-xs">
        <div className="flex items-center justify-between">
          <span className="text-[11px] text-slate-400 font-bold uppercase tracking-wider">
            📜 Kỳ Phổ Nước Đi ({game.moveHistory.length} nước)
          </span>
          <span className="text-[11px] text-cyan-400 font-bold">
            {game.lastMove ? game.lastMove.notationVi : 'Mới bắt đầu'}
          </span>
        </div>

        <div className="max-h-20 overflow-y-auto space-y-1 font-mono text-[12px] text-slate-300 pr-1 no-scrollbar">
          {game.moveHistory.length === 0 ? (
            <p className="text-slate-500 italic">Ván cờ khai cuộc. Bên Đỏ cầm quân đi trước.</p>
          ) : (
            game.moveHistory.slice(0, 6).map((m, idx) => (
              <div key={idx} className="flex justify-between border-b border-slate-800/50 pb-0.5">
                <span className={m.piece.color === 'red' ? 'text-rose-400 font-bold' : 'text-slate-200 font-bold'}>
                  {game.moveHistory.length - idx}. {m.piece.color === 'red' ? 'Đỏ' : 'Đen'}: {m.notationVi}
                </span>
                {m.captured && (
                  <span className="text-rose-400 font-medium text-[11px]">
                    (Ăn {PIECE_SYMBOLS[m.captured.type].viName})
                  </span>
                )}
              </div>
            ))
          )}
        </div>
      </div>
    </div>
  );
};

export default XiangqiGame;
